import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../theme';
import { useIsMobile } from '../hooks/useMediaQuery';

const PAGE_SIZE = 20;

const STATUS_COLORS = {
  DRAFT: '#facc15',
  PUBLISHED: theme.colors.success,
  DEPRECATED: theme.colors.textMuted,
};

const inputSt = {
  width: '100%', padding: '9px 12px',
  backgroundColor: theme.colors.background,
  border: `1px solid ${theme.colors.border}`,
  borderRadius: '7px', color: theme.colors.text,
  fontSize: '0.9rem', boxSizing: 'border-box', outline: 'none',
};

function Forms({ token }) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const [showCreate, setShowCreate] = useState(false);
  const [channels, setChannels] = useState([]);
  const [form, setForm] = useState({ name: '', channel_id: '', type: 'navigate' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [publishingId, setPublishingId] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = () => {
    setLoading(true);
    fetch(`/api/forms?page=${page}`, { headers })
      .then(r => r.ok ? r.json() : { data: [], total: 0 })
      .then(d => { setData(d.data || []); setTotal(d.total || 0); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [page, token]);

  const openCreate = async () => {
    setForm({ name: '', channel_id: '', type: 'navigate' });
    setCreateError('');
    const res = await fetch('/api/forms/channels', { headers });
    const ch = res.ok ? await res.json() : [];
    setChannels(ch);
    if (ch.length > 0) setForm(f => ({ ...f, channel_id: String(ch[0].id) }));
    setShowCreate(true);
  };

  const handleCreate = async () => {
    if (!form.name.trim() || !form.channel_id) return setCreateError('Nombre y canal son requeridos');
    setCreating(true);
    setCreateError('');
    try {
      const res = await fetch('/api/forms', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name, type: form.type, channel_id: Number(form.channel_id) }),
      });
      const d = await res.json();
      if (!res.ok) return setCreateError(d.message || 'Error al crear');
      setShowCreate(false);
      fetchData();
    } catch { setCreateError('Error de conexión'); }
    finally { setCreating(false); }
  };

  const handlePublish = async (id) => {
    setPublishingId(id);
    try {
      await fetch(`/api/forms/${id}/publish`, { method: 'POST', headers });
      fetchData();
    } finally { setPublishingId(null); }
  };

  const handleDelete = async (id) => {
    if (confirmDeleteId !== id) { setConfirmDeleteId(id); return; }
    setDeletingId(id);
    setConfirmDeleteId(null);
    try {
      await fetch(`/api/forms/${id}`, { method: 'DELETE', headers });
      fetchData();
    } finally { setDeletingId(null); }
  };

  return (
    <main style={{ padding: isMobile ? '1.2rem 0.8rem' : '2rem 1.5rem', maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ backgroundColor: theme.colors.surface, borderRadius: '14px', border: `1px solid ${theme.colors.border}`, padding: isMobile ? '1.2rem' : '2rem', boxShadow: `0 4px 24px ${theme.colors.glowSubtle}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h1 style={{ color: theme.colors.primary, fontSize: '1.4rem', margin: 0 }}>Formularios</h1>
          <button onClick={openCreate} style={{ padding: '9px 20px', backgroundColor: theme.colors.primary, color: theme.colors.background, border: 'none', borderRadius: '7px', fontSize: '0.9rem', fontWeight: '700', cursor: 'pointer' }}>
            + Crear
          </button>
        </div>

        {loading ? (
          <p style={{ color: theme.colors.textMuted, textAlign: 'center', padding: '2rem' }}>Cargando...</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
              <thead>
                <tr>
                  {['Nombre', 'Canal', 'Tipo', 'Estado', ''].map(col => (
                    <th key={col} style={{ textAlign: 'left', padding: '10px 12px', borderBottom: `1px solid ${theme.colors.border}`, color: theme.colors.primary, fontWeight: '600', whiteSpace: 'nowrap' }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: theme.colors.textMuted }}>Sin formularios. Crea el primero.</td></tr>
                ) : data.map(row => (
                  <tr key={row.id} style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
                    <td style={{ padding: '9px 12px', color: theme.colors.text, fontWeight: '500' }}>{row.name}</td>
                    <td style={{ padding: '9px 12px', color: theme.colors.textMuted }}>{row.channel_number || '—'}</td>
                    <td style={{ padding: '9px 12px', color: theme.colors.textMuted, fontSize: '0.82rem' }}>
                      {row.type === 'navigate' ? 'Navegación' : 'Con servidor'}
                    </td>
                    <td style={{ padding: '9px 12px' }}>
                      <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700', backgroundColor: `${STATUS_COLORS[row.status] || theme.colors.textMuted}22`, color: STATUS_COLORS[row.status] || theme.colors.textMuted, border: `1px solid ${STATUS_COLORS[row.status] || theme.colors.textMuted}` }}>
                        {row.status}
                      </span>
                    </td>
                    <td style={{ padding: '9px 12px' }}>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button onClick={() => navigate(`/formularios/${row.id}/editor`)} style={{ padding: '5px 12px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`, borderRadius: '6px', color: theme.colors.text, cursor: 'pointer', fontSize: '0.8rem' }}>
                          Editar
                        </button>
                        {row.status === 'DRAFT' && (
                          <button onClick={() => handlePublish(row.id)} disabled={publishingId === row.id} style={{ padding: '5px 12px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.success}`, borderRadius: '6px', color: theme.colors.success, cursor: 'pointer', fontSize: '0.8rem' }}>
                            {publishingId === row.id ? '...' : 'Publicar'}
                          </button>
                        )}
                        <button onClick={() => handleDelete(row.id)} disabled={deletingId === row.id} style={{ padding: '5px 12px', backgroundColor: confirmDeleteId === row.id ? theme.colors.error : 'transparent', border: `1px solid ${theme.colors.error}`, borderRadius: '6px', color: confirmDeleteId === row.id ? '#fff' : theme.colors.error, cursor: 'pointer', fontSize: '0.8rem' }}>
                          {confirmDeleteId === row.id ? '¿Confirmar?' : 'Eliminar'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1.2rem' }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: '6px 16px', borderRadius: '6px', border: `1px solid ${theme.colors.border}`, cursor: page === 1 ? 'not-allowed' : 'pointer', backgroundColor: 'transparent', color: page === 1 ? theme.colors.textMuted : theme.colors.text, fontSize: '0.88rem' }}>← Anterior</button>
          <span style={{ color: theme.colors.textMuted, fontSize: '0.88rem' }}>Página {page} de {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ padding: '6px 16px', borderRadius: '6px', border: `1px solid ${theme.colors.border}`, cursor: page === totalPages ? 'not-allowed' : 'pointer', backgroundColor: 'transparent', color: page === totalPages ? theme.colors.textMuted : theme.colors.text, fontSize: '0.88rem' }}>Siguiente →</button>
        </div>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div onClick={() => { if (!creating) setShowCreate(false); }} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: theme.colors.surface, borderRadius: '14px', border: `1px solid ${theme.colors.border}`, padding: isMobile ? '1.2rem' : '2rem', width: '100%', maxWidth: '440px', boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }}>
            <h2 style={{ color: theme.colors.primary, fontSize: '1.2rem', marginBottom: '1.5rem' }}>Nuevo formulario</h2>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', color: theme.colors.textMuted, fontSize: '0.85rem', marginBottom: '0.3rem' }}>Nombre</label>
              <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputSt} placeholder="ej: Formulario de contacto" />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', color: theme.colors.textMuted, fontSize: '0.85rem', marginBottom: '0.3rem' }}>Canal</label>
              <select value={form.channel_id} onChange={e => setForm(f => ({ ...f, channel_id: e.target.value }))} style={{ ...inputSt, cursor: 'pointer' }}>
                {channels.length === 0 && <option value="">Sin canales configurados</option>}
                {channels.map(ch => <option key={ch.id} value={ch.id}>{ch.number}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', color: theme.colors.textMuted, fontSize: '0.85rem', marginBottom: '0.6rem' }}>Tipo</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {[['navigate', 'Navegación autónoma', 'Meta maneja todo, no requiere servidor'], ['data_exchange', 'Con servidor', 'El servidor responde dinámicamente']].map(([val, label, desc]) => (
                  <label key={val} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer' }}>
                    <input type="radio" name="type" value={val} checked={form.type === val} onChange={() => setForm(f => ({ ...f, type: val }))} style={{ marginTop: '2px', accentColor: theme.colors.primary }} />
                    <span>
                      <span style={{ color: theme.colors.text, fontSize: '0.88rem', fontWeight: '600' }}>{label}</span>
                      <span style={{ display: 'block', color: theme.colors.textMuted, fontSize: '0.78rem' }}>{desc}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {createError && <p style={{ color: theme.colors.error, fontSize: '0.85rem', marginBottom: '1rem', textAlign: 'center' }}>{createError}</p>}

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setShowCreate(false)} disabled={creating} style={{ flex: 1, padding: '10px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`, borderRadius: '7px', color: theme.colors.textMuted, cursor: 'pointer', fontSize: '0.9rem' }}>Cancelar</button>
              <button onClick={handleCreate} disabled={creating} style={{ flex: 1, padding: '10px', backgroundColor: creating ? theme.colors.border : theme.colors.primary, border: 'none', borderRadius: '7px', color: theme.colors.background, cursor: creating ? 'not-allowed' : 'pointer', fontSize: '0.9rem', fontWeight: '700' }}>
                {creating ? 'Creando...' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default Forms;
